import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PLATFORM_CHART_COLOR, PLATFORM_LABEL, type SocialPlatform } from '@/client/types/marketing';
import type { CoverageItem } from '@/client/types/media';
import { publicMetrics } from '@/client/types/media';
import { usePerformanceTrackedQuery } from '@/client/api/marketing/radar';
import type { ApiCoverageItem, ApiPerformanceTracked, ApiTrackedKpi } from '@/client/types/marketing/api';
import {
  adaptCoverage,
  useAdaptedAccounts,
  useCoverageItems,
} from '@/client/pages/Admin/Media/coverage-api';
import { PlatformLogoBadge } from '@/client/pages/Admin/Media/components/PlatformLogo';
import { Button } from '@/components/ui/button';
import { EmptyState, KpiChange, Panel, StatusBadge } from './components/marketing-ui';
import { formatKpiValue } from './fixtures';
import { formatCompact, PUBLIC_RANGE_PRESETS } from './posting-insights-data';
import {
  loadPublicRangePreference,
  savePublicRangePreference,
  servedWindowLabel,
  utcDayKey,
  utcDayTick,
} from './performance-range-storage';

const SENTIMENT_BADGE = {
  positive: 'bg-emerald-50 text-emerald-700',
  neutral: 'bg-gray-100 text-gray-700',
  negative: 'bg-red-50 text-red-700',
  unknown: 'bg-amber-50 text-amber-800',
} as const;

const SUBJECT_LABEL: Record<string, string> = {
  brand: 'Brand',
  competitor: 'Competitor',
  topic: 'Topic',
};

const ENGAGEMENT_KEYS = ['likes', 'comments', 'shares', 'saves'] as const;

function metricDash(n: number | undefined): string {
  return n != null ? formatCompact(n) : '—';
}

function publicEngagement(item: CoverageItem): number {
  const m = publicMetrics(item);
  return ENGAGEMENT_KEYS.reduce((s, k) => {
    const v = m[k];
    return s + (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  }, 0);
}

function inClientRange(iso: string | undefined, rangeDays: number): boolean {
  if (!iso) return true; // undated — don't hide; aggregate may have dropped it
  const t = iso.includes('T')
    ? Date.parse(iso)
    : Date.parse(`${utcDayKey(iso)}T00:00:00Z`);
  if (!Number.isFinite(t)) return true;
  return t >= Date.now() - rangeDays * 86_400_000;
}

function trackedKpiLabel(kpi: ApiTrackedKpi): string {
  const label = kpi.label?.trim() || kpi.id;
  return /social/i.test(label) ? label : `${label} · social`;
}

/** Tolerate a double-wrapped envelope if the proxy ever leaves one in place. */
function asTrackedPayload(raw: unknown): ApiPerformanceTracked | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.summary && typeof r.summary === 'object') return raw as ApiPerformanceTracked;
  const nested = r.data;
  if (nested && typeof nested === 'object' && (nested as ApiPerformanceTracked).summary) {
    return nested as ApiPerformanceTracked;
  }
  return null;
}

function topPostRow(raw: ApiCoverageItem) {
  try {
    const item = adaptCoverage(raw);
    const m = publicMetrics(item);
    return {
      id: item.id || raw.id || String(Math.random()),
      platform: (item.platform ?? 'other') as SocialPlatform,
      handle: item.social?.authorHandle ?? item.author ?? '—',
      caption: item.social?.caption || item.summary || item.title || '(no caption)',
      publishedAt: item.publishedAt || '',
      url: item.url || '#',
      sentiment: item.sentiment,
      sourceIngest: item.sourceIngest,
      likes: m.likes,
      comments: m.comments,
      shares: m.shares,
      views: m.views,
    };
  } catch {
    return null;
  }
}

function rollupFromCoverage(items: CoverageItem[], rangeDays: number): ApiPerformanceTracked {
  const byPlatformMap = new Map<string, { posts: number; engagement: number }>();
  const byHandleMap = new Map<string, { posts: number; engagement: number }>();
  const bySubjectMap = new Map<string, { posts: number; engagement: number }>();

  for (const item of items) {
    if (item.channel !== 'social') continue;
    const eng = publicEngagement(item);
    const platform = item.platform || 'other';
    const handle = item.social?.authorHandle || item.author || 'unknown';
    const subject = item.subject || 'brand';

    const p = byPlatformMap.get(platform) ?? { posts: 0, engagement: 0 };
    p.posts += 1;
    p.engagement += eng;
    byPlatformMap.set(platform, p);

    const h = byHandleMap.get(handle) ?? { posts: 0, engagement: 0 };
    h.posts += 1;
    h.engagement += eng;
    byHandleMap.set(handle, h);

    const s = bySubjectMap.get(subject) ?? { posts: 0, engagement: 0 };
    s.posts += 1;
    s.engagement += eng;
    bySubjectMap.set(subject, s);
  }

  const totalEng = [...bySubjectMap.values()].reduce((n, r) => n + r.engagement, 0);
  const measured = items
    .filter((i) => i.channel === 'social' && publicEngagement(i) > 0)
    .sort((a, b) => publicEngagement(b) - publicEngagement(a))
    .slice(0, 10);

  return {
    range_days: rangeDays,
    measurement_basis: 'public',
    summary: {
      posts: [...byPlatformMap.values()].reduce((n, r) => n + r.posts, 0),
      handles: byHandleMap.size,
      public_engagement: [...byPlatformMap.values()].reduce((n, r) => n + r.engagement, 0),
    },
    by_platform: [...byPlatformMap.entries()].map(([platform, r]) => ({
      platform,
      posts: r.posts,
      engagement: r.engagement,
    })),
    by_handle: [...byHandleMap.entries()].map(([handle, r]) => ({
      handle,
      posts: r.posts,
      engagement: r.engagement,
    })),
    share_of_voice: [...bySubjectMap.entries()].map(([subject, r]) => ({
      subject,
      posts: r.posts,
      engagement: r.engagement,
      share: totalEng > 0 ? r.engagement / totalEng : null,
    })),
    top_posts: measured.map((item) => ({
      id: item.id,
      channel: item.channel,
      kind: item.kind,
      media_class: item.mediaClass,
      subject: item.subject,
      title: item.title,
      summary: item.summary,
      body_excerpt: item.bodyExcerpt ?? null,
      url: item.url ?? null,
      url_canonical: null,
      author: item.author ?? null,
      site_name: null,
      outlet_id: item.outletId != null ? Number(item.outletId) : null,
      outlet_name: item.outletName ?? null,
      language: item.language,
      sentiment: item.sentiment,
      status: item.status,
      urgency_score: item.urgencyScore,
      play_id: null,
      source_ingest: item.sourceIngest,
      internal_note: item.internalNote ?? null,
      captured_at: item.capturedAt,
      published_at: item.publishedAt,
      platform: item.platform,
      author_handle: item.social?.authorHandle ?? null,
      said: item.social?.accountId != null ? Number(item.social.accountId) : null,
      metrics: item.metrics as unknown as Record<string, number>,
      metrics_public: item.metricsPublic as unknown as Record<string, number>,
      measurement_basis: item.measurementBasis ?? 'public',
    })) as ApiCoverageItem[],
  };
}

const TrackedListeningInsightsPage = () => {
  const accounts = useAdaptedAccounts();
  const listenCount = accounts.filter((a) => a.useAsListen && !a.owned).length;

  const [platform, setPlatform] = useState<SocialPlatform | 'all'>('all');
  const [handle, setHandle] = useState<string | 'all'>('all');
  const [subject, setSubject] = useState<'all' | 'brand' | 'competitor'>('all');
  const [range, setRange] = useState(() => loadPublicRangePreference());

  useEffect(() => {
    savePublicRangePreference(range);
  }, [range]);

  const trackedParams = useMemo(
    () => ({
      range_days: range,
      ...(platform !== 'all' ? { platform } : {}),
      ...(handle !== 'all' ? { handle } : {}),
      ...(subject !== 'all' ? { subject } : {}),
    }),
    [range, platform, handle, subject],
  );

  const tracked = usePerformanceTrackedQuery(trackedParams);

  // Coverage fallback: no server from/to (unstable keys + drops undated rows).
  // Fetch social once; narrow by range/platform/handle client-side.
  const coverageParams = useMemo(
    () => ({
      channel: 'social' as const,
      limit: 500,
      ...(platform !== 'all' ? { platform } : {}),
    }),
    [platform],
  );
  const coverage = useCoverageItems(coverageParams);

  const apiData = asTrackedPayload(tracked.data);
  const apiPosts = apiData?.summary?.posts ?? 0;
  const servedDays = apiData?.range_days ?? range;

  const fallbackData = useMemo(() => {
    if (apiPosts > 0) return null;
    let items = coverage.items.filter((i) => inClientRange(i.publishedAt, servedDays));
    if (handle !== 'all') {
      items = items.filter(
        (i) => (i.social?.authorHandle || i.author || '') === handle,
      );
    }
    if (subject !== 'all') {
      items = items.filter((i) => (i.subject || 'brand') === subject);
    }
    return rollupFromCoverage(items, servedDays);
  }, [apiPosts, coverage.items, handle, servedDays, subject]);

  const data = apiPosts > 0 ? apiData : fallbackData;
  const usingFallback = apiPosts === 0 && (fallbackData?.summary.posts ?? 0) > 0;
  const archiveSocialCount = coverage.data?.total ?? coverage.items.length;

  const top = useMemo(
    () =>
      (data?.top_posts ?? [])
        .map(topPostRow)
        .filter((p): p is NonNullable<typeof p> => p != null),
    [data?.top_posts],
  );

  const byPlatform = useMemo(
    () =>
      (data?.by_platform ?? []).map((r) => ({
        platform: PLATFORM_LABEL[r.platform as SocialPlatform] ?? r.platform,
        key: r.platform,
        posts: r.posts,
        engagement: r.engagement,
        fill: PLATFORM_CHART_COLOR[r.platform as SocialPlatform] ?? '#64748b',
      })),
    [data?.by_platform],
  );

  const byHandle = data?.by_handle ?? [];
  const sov = data?.share_of_voice ?? [];
  const trackedKpis = data?.kpis ?? [];
  const metricSeries = useMemo(
    () =>
      (data?.series?.points ?? []).map((p) => ({
        date: utcDayKey(p.date),
        value: p.value ?? null,
      })),
    [data?.series],
  );

  const platformOptions = useMemo(() => {
    const fromApi = (data?.by_platform ?? []).map((r) => r.platform as SocialPlatform);
    return [
      ...new Set(
        fromApi.length
          ? fromApi
          : (['x', 'instagram', 'facebook', 'linkedin', 'tiktok'] as SocialPlatform[]),
      ),
    ];
  }, [data?.by_platform]);

  const handleOptions = useMemo(
    () => [...new Set((data?.by_handle ?? []).map((r) => r.handle).filter(Boolean))].sort(),
    [data?.by_handle],
  );

  const isLoading = (tracked.isLoading || tracked.isPending) && (coverage.isLoading || coverage.isPending);
  const isError = tracked.isError && coverage.isError;
  const isRefreshing = tracked.isFetching || coverage.isFetching;
  const posts = data?.summary?.posts ?? 0;

  const reload = () => {
    void Promise.all([tracked.refetch(), coverage.refetch()]);
  };

  const emptyHint = (() => {
    if (tracked.isError && !coverage.isError) {
      return 'Tracked rollup failed; coverage fallback also empty for this window.';
    }
    if (listenCount === 0) {
      return 'No listen-target accounts yet. Add handles under Sources → Accounts (listen on, owned off), Sync watchers, then Run discovery.';
    }
    if (archiveSocialCount > 0 && apiPosts === 0) {
      return `Radar has ${archiveSocialCount} social coverage row(s), but none qualify for Tracked in the last ${servedDays} days (need ISO published_at + public engagement). Run discovery or widen the range.`;
    }
    return `No public-surface social posts in the last ${servedDays} days. Enable Apify, Sync watchers on Listening, Run discovery — or paste a post URL into Coverage.`;
  })();

  return (
    <>
      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
        <p className="font-medium">Public measurement only (`metrics_public`).</p>
        <p className="mt-1 text-amber-900/90">
          Not Zernio. Tracked is social only — news and traditional sit on Sentiment, a different
          corpus. Listen targets ({listenCount || '—'} handles with listen on, owned off) never get
          impressions or connected reach from the publishing rail. Every figure here is the uniform
          public bag — comparable across us and competitors.
          {usingFallback
            ? ' Rollup endpoint was empty; showing live coverage for this window instead.'
            : ''}
        </p>
        <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[13px]">
          <Link to="/admin/marketing/sources/accounts" className="font-medium text-amber-950 underline">
            Sources → Accounts
          </Link>
          <Link to="/admin/marketing/sources/listening" className="font-medium text-amber-950 underline">
            Listening rules
          </Link>
          <Link
            to="/admin/marketing/radar/coverage/social"
            className="font-medium text-amber-950 underline"
          >
            Coverage archive
          </Link>
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value as SocialPlatform | 'all')}
          className="h-9 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none ring-blue-500 focus:ring-2"
        >
          <option value="all">All platforms</option>
          {platformOptions.map((p) => (
            <option key={p} value={p}>
              {PLATFORM_LABEL[p] ?? p}
            </option>
          ))}
        </select>
        <select
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          className="h-9 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none ring-blue-500 focus:ring-2"
        >
          <option value="all">All handles</option>
          {handleOptions.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <select
          value={subject}
          onChange={(e) => setSubject(e.target.value as typeof subject)}
          className="h-9 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none ring-blue-500 focus:ring-2"
        >
          <option value="all">All subjects</option>
          <option value="brand">Brand</option>
          <option value="competitor">Competitor</option>
        </select>
        <select
          value={String(range)}
          onChange={(e) => setRange(Number(e.target.value))}
          className="h-9 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none ring-blue-500 focus:ring-2"
        >
          {PUBLIC_RANGE_PRESETS.map((d) => (
            <option key={d} value={d}>
              Last {d} days
            </option>
          ))}
        </select>
        <span className="text-xs tabular-nums text-gray-400">
          {servedWindowLabel(data?.range_days, range)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 gap-1.5"
          disabled={isRefreshing}
          onClick={reload}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          Reload
        </Button>
        {isRefreshing && !isLoading && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">Updating…</span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-8 text-sm text-gray-500 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading tracked performance…
        </div>
      ) : isError ? (
        <EmptyState label="Tracked performance and coverage both failed to load. Check your session and try Reload." />
      ) : posts === 0 && trackedKpis.length === 0 ? (
        <div className="space-y-3">
          <EmptyState label={emptyHint} />
          <p className="text-center text-[11px] text-gray-400">
            API rollup: {tracked.isError ? 'error' : `${apiPosts} posts`}
            {' · '}
            Coverage archive: {coverage.isError ? 'error' : `${archiveSocialCount} social`}
            {' · '}
            Listen targets: {listenCount}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {trackedKpis.length > 0
              ? trackedKpis.map((kpi) => (
                  <div
                    key={`${kpi.id}:${kpi.basis ?? 'none'}`}
                    className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        {trackedKpiLabel(kpi)}
                      </p>
                      <KpiChange change={kpi.change_pct} value={kpi.value} />
                    </div>
                    <p className="mt-1 text-2xl font-semibold tabular-nums">{formatKpiValue(kpi)}</p>
                    <p className="mt-1 text-xs text-gray-400">
                      {kpi.basis
                        ? `basis: ${kpi.basis}`
                        : kpi.id === 'kpi_ctr'
                          ? 'public scrape does not see clicks'
                          : '—'}
                      {kpi.id === 'kpi_engagement_rate' && data!.totals && data!.totals.posts > 0
                        ? ` · ${data!.totals.reach_posts} of ${data!.totals.posts} posts reported reach`
                        : ''}
                    </p>
                  </div>
                ))
              : (
                <>
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Social posts captured
                    </p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums">{data!.summary.posts}</p>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Public social engagement
                    </p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums">
                      {formatCompact(data!.summary.public_engagement)}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">Sum of present public keys — not reach</p>
                  </div>
                </>
              )}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Social handles
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{data!.summary.handles}</p>
            </div>
          </div>

          {metricSeries.some((p) => p.value != null) ? (
            <Panel
              title={`${data?.series?.metric ?? 'Public metric'} over time`}
              className="mb-4"
            >
              <p className="mb-2 text-xs text-gray-400">
                Tracked series from public coverage. Missing days stay gaps, not zeros.
              </p>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={metricSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={utcDayTick} />
                    <YAxis tick={{ fontSize: 11 }} width={36} allowDecimals={false} />
                    <Tooltip labelFormatter={(d: string) => utcDayKey(d) || d} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name={data?.series?.metric ?? 'Value'}
                      stroke="#0f172a"
                      fill="#0f172a"
                      fillOpacity={0.12}
                      strokeWidth={1.5}
                      dot={{ r: 2, fill: '#0f172a', strokeWidth: 0 }}
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          ) : null}

          {sov.length > 0 && (
            <Panel title="Share of voice" className="mb-4">
              <p className="mb-3 text-xs text-gray-500">
                Grouped by derived <code className="text-[11px]">subject</code> over{' '}
                <code className="text-[11px]">metrics_public</code>. Share is null when nothing reported
                engagement.
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                {sov.map((row) => (
                  <div
                    key={row.subject}
                    className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5"
                  >
                    <p className="text-xs font-medium text-gray-500">
                      {SUBJECT_LABEL[row.subject] ?? row.subject}
                    </p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">
                      {row.share != null ? `${(row.share * 100).toFixed(0)}%` : '—'}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {row.posts} posts · {formatCompact(row.engagement)} eng
                    </p>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <div className="mb-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Posts per platform">
              <div className="h-52">
                {byPlatform.length === 0 ? (
                  <p className="flex h-full items-center justify-center text-sm text-gray-400">No platforms</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byPlatform} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                      <Tooltip />
                      <Bar dataKey="posts" name="Posts" radius={[4, 4, 0, 0]}>
                        {byPlatform.map((r) => (
                          <Cell key={r.key} fill={r.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Panel>
            <Panel title="Engagement by handle">
              <div className="h-52">
                {byHandle.length === 0 ? (
                  <p className="flex h-full items-center justify-center text-sm text-gray-400">No handles</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byHandle} layout="vertical" margin={{ left: 8, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="handle" width={110} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="engagement" name="Engagement" fill="#0f172a" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Panel>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-900">
              Top posts (measured public engagement only)
            </div>
            {top.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">
                Posts were captured but none reported public engagement figures yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Post</th>
                      <th className="px-3 py-2 font-medium">Ingest</th>
                      <th className="px-3 py-2 font-medium">Likes</th>
                      <th className="px-3 py-2 font-medium">Comments</th>
                      <th className="px-3 py-2 font-medium">Shares</th>
                      <th className="px-3 py-2 font-medium">Views</th>
                      <th className="px-3 py-2 font-medium">Sentiment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {top.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <div className="flex items-start gap-2">
                            <PlatformLogoBadge platform={p.platform} size="sm" />
                            <div className="min-w-0">
                              {p.url && p.url !== '#' ? (
                                <a
                                  href={p.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="line-clamp-2 font-medium text-gray-900 hover:underline"
                                >
                                  {p.caption}
                                </a>
                              ) : (
                                <p className="line-clamp-2 font-medium text-gray-900">{p.caption}</p>
                              )}
                              <p className="text-[11px] text-gray-400">
                                {p.handle} · {utcDayKey(p.publishedAt) || '—'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-gray-600">{p.sourceIngest}</td>
                        <td className="px-3 py-2.5 tabular-nums">{metricDash(p.likes)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{metricDash(p.comments)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{metricDash(p.shares)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{metricDash(p.views)}</td>
                        <td className="px-3 py-2.5">
                          <StatusBadge label={p.sentiment} className={SENTIMENT_BADGE[p.sentiment]} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default TrackedListeningInsightsPage;
